/* eslint-disable max-len */
import React from "react";
import {Link} from "react-router-dom";
import {Box} from "@mui/material";
import {PoliciesStyledBox} from "lib/components";

export default function TermsConditions() {
  return (
    <PoliciesStyledBox>
      <Box sx={{width: {xs: "100%", sm: "90%"}}}>
        <h3>Terms and Conditions</h3>
        <p>
          <strong>Last updated: April 5, 2025</strong>
        </p>
        <p>Please read these Terms and Conditions carefully before using Our Service.</p>

        <h3>Interpretation and Definitions</h3>
        <h4>Interpretation</h4>
        <p>
          Words with initial capitalization have meanings defined below. These definitions apply whether the words
          appear in singular or plural form.
        </p>
        <h4>Definitions</h4>
        <p>For the purposes of these Terms and Conditions:</p>
        <p>
          <strong>Affiliate</strong>: An entity that controls, is controlled by, or is under common control with a
          party, where &quot;control&quot; means ownership of 50% or more of shares, equity interest, or other
          securities entitled to vote for election of directors or other managing authority.
        </p>
        <p>
          <strong>Account</strong>: A unique account created for You to access our Service or parts of our Service.
        </p>
        <p>
          <strong>Company</strong>: Refers to Maritime IT Solutions LLC (also &quot;the Company,&quot; &quot;We,&quot;
          &quot;Us,&quot; or &quot;Our&quot;), a Delaware-based company with operations in the United Arab Emirates.
        </p>
        <p>
          <strong>Country</strong>: Refers to the United States of America.
        </p>
        <p>
          <strong>Crew Data</strong>: Personal information, professional certificates, and other maritime-related data
          uploaded or managed by You on the Service.
        </p>
        <p>
          <strong>Device</strong>: Any device that can access the Service, such as a computer, cellphone, or digital
          tablet.
        </p>
        <p>
          <strong>Feedback</strong>: Suggestions, innovations, or comments sent by You regarding the Service.
        </p>
        <p>
          <strong>Free Trial</strong>: A limited period during which You may use the Service for free.
        </p>
        <p>
          <strong>Service</strong>: The CrewBook platform, accessible at{" "}
          <a href="https://www.crewbook.app">https://www.crewbook.app</a>.
        </p>
        <p>
          <strong>Subscriptions</strong>: Paid access to the Service on a recurring basis.
        </p>
        <p>
          <strong>Terms and Conditions</strong>: This agreement (also &quot;Terms&quot;) between You and the Company.
        </p>
        <p>
          <strong>Third-party Social Media Service</strong>: Services or content provided by a third party that may be
          included in the Service.
        </p>
        <p>
          <strong>You</strong>: The individual or entity using the Service.
        </p>
        <h3>Acknowledgment</h3>
        <p>
          These Terms and Conditions govern your use of <strong>CrewBook</strong>, a secure platform designed by
          seafarers for seafarers to manage personal crew data and professional certificates (the &quot;Service&quot;).
          By accessing or using the Service, you agree to these Terms, forming a binding agreement between you and{" "}
          <strong>Maritime IT Solutions LLC</strong>.
        </p>
        <p>
          Your access to and use of the Service is conditioned on your acceptance of and compliance with these Terms. If
          you disagree with any part, you may not use the Service.
        </p>
        <p>
          You represent that you are over the age of 18. The Company does not permit those under 18 to use the Service.
        </p>
        <p>
          <strong>Your Responsibilities</strong>: You agree to provide accurate, complete, and up-to-date Crew Data and
          to maintain its accuracy. CrewBook is a tool for managing maritime-related information and is not a substitute
          for official verification of certificates or compliance with maritime regulations.
        </p>
        <p>
          Your use of the Service is also subject to Our <strong>Privacy Policy</strong>, which explains how we collect,
          use, and protect your personal and professional information. Please review it carefully.
        </p>
        <h3>Subscriptions</h3>
        <h4>Subscription Period</h4>
        <p>
          The Service is available only with a paid Subscription. You will be billed in advance on a recurring basis
          (e.g., monthly or annually), depending on your chosen plan. Your Subscription will automatically renew under
          the same conditions unless canceled.
        </p>
        <h4>Subscription Cancellations</h4>
        <p>
          You may cancel your Subscription through your Account settings or by contacting Us. Cancellations take effect
          at the end of the current billing period, and you will retain access until then. No refunds are provided for
          the current period.
        </p>
        <h4>Billing</h4>
        <p>
          You must provide accurate billing information (e.g., full name, address, payment method). If automatic billing
          fails, We will issue an invoice for manual payment.
        </p>
        <h4>Fee Changes</h4>
        <p>
          We may modify Subscription fees at any time, with reasonable notice. Changes take effect at the start of the
          next billing period. Continued use after a fee change constitutes acceptance.
        </p>
        <h4>Refunds</h4>
        <p>Paid Subscription fees are non-refundable, except as required by law or at Our discretion.</p>
        <h3>Free Trial</h3>
        <p>
          We may offer a Free Trial for a limited period. You may be required to provide billing information to continue
          using the Service after the trial ends. If you do not cancel before the trial period expires, you will be
          charged the applicable Subscription fee.
        </p>
        <h3>User Accounts</h3>
        <p>
          When you create an Account, you must provide accurate and complete information. You are responsible for
          safeguarding your Account password and for any activities under your Account. Notify Us immediately of any
          unauthorized use.
        </p>
        <h3>Intellectual Property</h3>
        <p>
          The Service and its content (excluding Crew Data) are owned by the Company or its licensors. You are granted a
          limited, non-exclusive license to use the Service under these Terms. No ownership rights are transferred.
        </p>
        <h3>Your Feedback to Us</h3>
        <p>Any Feedback you provide is assigned to Us. We may use it without restriction or compensation to you.</p>
        <h3>Links to Other Websites</h3>
        <p>
          Our Service may contain links to third-party websites. We are not responsible for their content or practices.
        </p>
        <h3>Termination</h3>
        <p>
          We may terminate or suspend your Account for violating these Terms, with or without notice. Upon termination,
          your right to use the Service ceases immediately.
        </p>
        <h3>Data Retention and Deletion</h3>
        <p>Upon termination, We may retain or delete your Crew Data as per Our Privacy Policy and applicable laws.</p>
        <h3>Limitation of Liability</h3>
        <p>
          To the fullest extent permitted by law, the Company and its Affiliates shall not be liable for any indirect,
          incidental, or consequential damages arising from your use of the Service.
        </p>
        <p>Our total liability shall not exceed the amount you paid Us in the past 12 months.</p>
        <h3>&quot;AS IS&quot; and &quot;AS AVAILABLE&quot; Disclaimer</h3>
        <p>
          The Service is provided &quot;AS IS&quot; and &quot;AS AVAILABLE,&quot; without warranties of any kind. We do
          not guarantee uninterrupted or error-free operation.
        </p>
        <h3>Governing Law</h3>
        <p>
          These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict of law
          principles.
        </p>
        <h3>Disputes Resolution</h3>
        <p>
          Any disputes shall be resolved through binding arbitration in Delaware, USA, under the rules of the American
          Arbitration Association.
        </p>
        <h3>For European Union (EU) Users</h3>
        <p>If you are an EU consumer, you may benefit from mandatory provisions of your country’s law.</p>
        <h3>United States Federal Government End Use Provisions</h3>
        <p>
          If you are a U.S. federal government end user, the Service is a &quot;Commercial Item&quot; as defined in 48
          C.F.R. §2.101.
        </p>
        <h3>United States Legal Compliance</h3>
        <p>
          You represent that you are not located in a country subject to U.S. government embargoes or designated as a
          &quot;terrorist supporting&quot; country.
        </p>
        <h3>Severability and Waiver</h3>
        <p>
          If any provision is unenforceable, the remaining provisions remain in effect. No waiver of any term is binding
          unless in writing.
        </p>
        <h3>Translation Interpretation</h3>
        <p>If these Terms are translated, the English version prevails in case of discrepancies.</p>
        <h3>Changes to These Terms and Conditions</h3>
        <p>
          We may update these Terms at Our discretion. Changes are effective upon posting, and continued use constitutes
          acceptance.
        </p>
        <h3>Contact Us</h3>
        <p>For questions about these Terms, contact Us:</p>
        <p>
          - <strong>By email</strong>: <a href="mailto:watchkeeper@crewbook.app">watchkeeper@crewbook.app</a>
        </p>
        <p>
          - <strong>By visiting</strong>: <Link to="/contact">https://crewbook.app/contact</Link>
        </p>
      </Box>
    </PoliciesStyledBox>
  );
}
